const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;

const ALLOWED_EVENT_PARAMS = {
  sign_up: new Set(['method', 'organization_created']),
  login: new Set(['method', 'redirect_target']),
  generate_lead: new Set(['plan', 'current_plan', 'source']),
  begin_checkout: new Set(['plan', 'currency', 'value', 'source']),
  purchase: new Set(['transaction_id', 'currency', 'value', 'plan']),
  scan_created: new Set(['scanner', 'target_type', 'plan', 'source']),
  report_created: new Set(['report_type', 'finding_count_bucket']),
  support_ticket_created: new Set(['ticket_type', 'priority', 'source'])
};

const MAX_PARAM_KEY_LENGTH = 40;
const MAX_STRING_VALUE_LENGTH = 80;

function isGa4Enabled() {
  return Boolean(GA4_MEASUREMENT_ID) && typeof window !== 'undefined' && typeof document !== 'undefined';
}

function normalizeEventName(eventName) {
  const normalized = String(eventName || '').trim();
  return Object.prototype.hasOwnProperty.call(ALLOWED_EVENT_PARAMS, normalized) ? normalized : null;
}

function normalizeEventParamKey(key) {
  const normalized = String(key || '').trim();
  if (!normalized || normalized.length > MAX_PARAM_KEY_LENGTH) return null;
  if (!/^[a-z][a-z0-9_]*$/.test(normalized)) return null;
  return normalized;
}

function normalizeEventParamValue(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;

  const normalized = String(value ?? '').trim();
  if (!normalized) return undefined;
  return normalized.slice(0, MAX_STRING_VALUE_LENGTH);
}

export function sanitizeEventParams(eventName, params = {}) {
  const allowedParams = ALLOWED_EVENT_PARAMS[eventName];
  if (!allowedParams || !params || typeof params !== 'object') return {};

  return Object.entries(params).reduce((sanitized, [key, value]) => {
    const normalizedKey = normalizeEventParamKey(key);
    if (!normalizedKey || !allowedParams.has(normalizedKey)) return sanitized;

    const normalizedValue = normalizeEventParamValue(value);
    if (normalizedValue === undefined) return sanitized;

    return { ...sanitized, [normalizedKey]: normalizedValue };
  }, {});
}

export function initGA4() {
  if (!isGa4Enabled() || window.gtag) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_MEASUREMENT_ID)}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID, {
    page_path: window.location.pathname + window.location.search,
    page_title: document.title
  });
}

export function trackPageView(location = window.location) {
  if (!isGa4Enabled() || !window.gtag) return;

  const pagePath = `${location.pathname || '/'}${location.search || ''}`;
  window.gtag('config', GA4_MEASUREMENT_ID, {
    page_path: pagePath,
    page_title: document.title
  });
}

export function trackEvent(eventName, params = {}) {
  const normalizedEventName = normalizeEventName(eventName);
  if (!normalizedEventName || !isGa4Enabled() || !window.gtag) return;

  window.gtag('event', normalizedEventName, sanitizeEventParams(normalizedEventName, params));
}
